import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Printer, Download, Building2, Mail, Phone, Globe } from "lucide-react";
import { formatCurrency, formatDate, formatQuantity } from "@/lib/format";

/**
 * Offentlig kundevisning av et tilbud – ingen innlogging.
 * Henter data via /api/public/q/:token som samtidig registrerer åpningen.
 */
export const Route = createFileRoute("/q/$token")({
  component: PublicQuoteView,
  head: () => ({
    meta: [
      { title: "Tilbud" },
      { name: "description", content: "Se ditt tilbud fra fagbedriften." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type ApiResp = {
  quote?: any;
  items?: any[];
  company?: any;
  customer?: any;
  error?: string;
};

function PublicQuoteView() {
  const { token } = Route.useParams();
  const { data, isLoading, error } = useQuery<ApiResp>({
    queryKey: ["public-quote", token],
    queryFn: async () => {
      const r = await fetch(`/api/public/q/${token}`);
      const body = await r.json();
      if (!r.ok) throw new Error(body?.error ?? "Kunne ikke laste tilbudet");
      return body;
    },
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return <div className="mx-auto max-w-3xl p-8 text-center text-muted-foreground">Laster tilbud…</div>;
  }
  if (error || data?.error || !data?.quote) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <h1 className="mb-2 text-2xl font-semibold">Tilbudet er ikke tilgjengelig</h1>
        <p className="text-muted-foreground">
          Lenken kan være utløpt, trukket tilbake eller feilstavet. Ta kontakt med avsender.
        </p>
      </div>
    );
  }

  const { quote, items = [], company, customer } = data;
  const now = new Date();
  const valid = quote.valid_until ? new Date(quote.valid_until) : null;
  const expired = valid ? valid < now : false;

  return (
    <div className="mx-auto max-w-3xl p-4 md:p-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div className="text-sm text-muted-foreground">
          Tilbud fra <b className="text-foreground">{company?.name ?? ""}</b>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="btn btn-secondary">
            <Download className="h-4 w-4" /> Last ned / skriv ut
          </button>
        </div>
      </div>

      <article className="rounded-lg border border-border bg-card p-6 shadow-sm md:p-10 print:border-0 print:shadow-none">
        <header className="mb-8 flex flex-wrap items-start justify-between gap-6 border-b border-border pb-6">
          <div>
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="mb-3 max-h-16" />
            ) : (
              <div className="mb-2 flex items-center gap-2 text-lg font-semibold">
                <Building2 className="h-5 w-5" /> {company?.name}
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              {company?.address && <div>{company.address}</div>}
              {(company?.postal_code || company?.city) && (
                <div>{[company?.postal_code, company?.city].filter(Boolean).join(" ")}</div>
              )}
              {company?.org_number && <div>Org.nr {company.org_number}</div>}
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="text-2xl font-semibold">Tilbud</div>
            <div className="text-muted-foreground">Nr {quote.quote_number}</div>
            {quote.sent_at && (
              <div className="text-muted-foreground">Sendt: {formatDate(quote.sent_at)}</div>
            )}
            {valid && (
              <div className={expired ? "text-destructive" : "text-muted-foreground"}>
                Gyldig til: {formatDate(quote.valid_until!)}
                {expired && " (utløpt)"}
              </div>
            )}
          </div>
        </header>

        {customer && (
          <section className="mb-6">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Til</div>
            <div className="font-medium">{customer.name}</div>
            {customer.address && (
              <div className="text-sm text-muted-foreground">
                {customer.address}
                {(customer.postal_code || customer.city) && (
                  <>, {[customer.postal_code, customer.city].filter(Boolean).join(" ")}</>
                )}
              </div>
            )}
          </section>
        )}

        <h1 className="mb-2 text-xl font-semibold">{quote.title}</h1>
        {quote.customer_summary && (
          <p className="mb-6 whitespace-pre-wrap text-sm text-muted-foreground">{quote.customer_summary}</p>
        )}

        <section className="mb-6 overflow-hidden rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Beskrivelse</th>
                <th className="px-3 py-2 text-right">Antall</th>
                <th className="px-3 py-2 text-right">Enhet</th>
                <th className="px-3 py-2 text-right">Pris</th>
                <th className="px-3 py-2 text-right">Sum</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i, idx) => (
                <tr key={idx} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div>{i.description}</div>
                    {i.customer_note && (
                      <div className="text-xs text-muted-foreground">{i.customer_note}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{formatQuantity(Number(i.quantity ?? 0))}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{i.unit}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(Number(i.unit_price ?? 0))}</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrency(Number(i.unit_price ?? 0) * Number(i.quantity ?? 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <div className="mb-8 flex justify-end">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
              <span>Total inkl. mva</span>
              <span>{formatCurrency(Number(quote.total_incl_vat ?? 0))}</span>
            </div>
          </div>
        </div>

        {(quote.terms_override || quote.notes) && (
          <section className="mb-6">
            <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Vilkår</div>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {quote.terms_override || quote.notes}
            </p>
          </section>
        )}

        <footer className="mt-10 border-t border-border pt-4 text-xs text-muted-foreground">
          <div className="mb-1 font-medium text-foreground">Kontakt</div>
          <div className="flex flex-wrap gap-4">
            {company?.email && (
              <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {company.email}</span>
            )}
            {company?.phone && (
              <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {company.phone}</span>
            )}
            {company?.website && (
              <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> {company.website}</span>
            )}
          </div>
        </footer>
      </article>
    </div>
  );
}
